import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Server } from './types'
import { getServers } from '../api/serverRepository'
import { SentryService } from '@/shared/lib/sentry/sentryService'

export const useServerStore = defineStore('server', () => {
  const servers = ref<Server[]>([])
  const selectedServer = ref<Server | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  async function fetchServers() {
    if (isLoading.value) {
      console.log('Запрос серверов уже выполняется, пропускаем')
      return
    }

    try {
      isLoading.value = true
      error.value = null
      servers.value = []

      SentryService.addBreadcrumb('Загрузка списка серверов', 'server', 'info')

      const response = await getServers()

      console.log('Servers API response:', response.data)

      if (response.data && Array.isArray(response.data)) {
        servers.value = response.data
        console.log('Set servers from response:', response.data)

        SentryService.setContext('servers_loaded', {
          servers_count: response.data.length,
          servers: response.data.map(server => ({
            id: server.id,
            name: server.name,
            protocol: server.protocol
          }))
        })

        SentryService.captureMessage('Серверы успешно загружены', 'info')
        SentryService.addBreadcrumb(`Загружено ${response.data.length} серверов`, 'server', 'info')
      }
    } catch (err: any) {
      error.value = err instanceof Error ? err.message : 'Неизвестная ошибка при загрузке серверов'
      servers.value = []

      SentryService.setContext('servers_load_error', {
        error_message: error.value,
        error_status: err.status || 'unknown',
        error_type: err instanceof Error ? err.constructor.name : typeof err
      })

      SentryService.captureException(err, {
        context: 'fetchServers',
        errorMessage: error.value
      })

      console.error('Ошибка при загрузке серверов:', err)
    } finally {
      isLoading.value = false
    }
  }

  function selectServer(server: Server) {
    selectedServer.value = server
    
    SentryService.addBreadcrumb(`Выбран сервер: ${server.name}`, 'server', 'info')
    console.log('Выбран сервер:', server)
  }

  function clearSelection() {
    selectedServer.value = null
    error.value = null
  }

  function clearAll() {
    servers.value = []
    selectedServer.value = null
    error.value = null
  }

  return {
    servers,
    selectedServer,
    isLoading,
    error,
    fetchServers,
    selectServer,
    clearSelection,
    clearAll
  }
})
